import GameInvitationModal, { GameInvitation } from '@/components/game-setup/GameInvitationModal';
import { mediumImpact } from '@/utils/capacitorUtils';
import { ToastOptions, useIonToast } from '@ionic/react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from './UserProfileContext';
import { supabase } from '@/utils/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { api } from '@/utils/api';

interface NotificationContextType {
  showNotification: (message: string, title?: string, icon?: string, onClick?: () => void) => void;
  showGameInvitation: (invitation: GameInvitation) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [presentToast] = useIonToast();
  const { profile } = useUserProfile();
  const { t } = useTranslation();
  const userId = profile?.userId;

  // State for game invitation modal
  const [showModal, setShowModal] = useState(false);
  const [currentInvitation, setCurrentInvitation] = useState<GameInvitation | null>(null);

  // Show a notification toast
  const showNotification = (
    message: string,
    title: string = '',
    icon?: string,
    onClick?: () => void
  ) => {
    const toastOptions: ToastOptions = {
      message: message,
      header: title,  // Use title as the header for the toast
      position: 'top',
      icon: icon || 'notifications-outline',
      cssClass: 'notification-toast',
      buttons: [
        ...(onClick ? [{
          text: t('join'),
          handler: onClick,
          role: 'view'
        }] : []),
        {
          text: t('decline'),
          role: 'cancel'
        }
      ],
      layout: 'stacked',
      htmlAttributes: {
        'data-allow-html': 'true'
      }
    };

    presentToast(toastOptions);
  };

  // Show game invitation modal or toast based on game time
  const showGameInvitation = (invitation: GameInvitation) => {
    // Check if the game is for now (within the next hour) or for the future
    const gameDate = typeof invitation.gameDate === 'string'
      ? new Date(invitation.gameDate)
      : invitation.gameDate;

    const gameTime = invitation.gameTime.split(':');
    const gameDateTime = new Date(gameDate);
    gameDateTime.setHours(parseInt(gameTime[0]), parseInt(gameTime[1] || '0'));

    const now = new Date();
    const timeDiff = gameDateTime.getTime() - now.getTime();
    const isImmediateGame = timeDiff <= 60 * 60 * 1000; // 1 hour or less

    if (isImmediateGame) {
      // Show modal for immediate games
      setCurrentInvitation(invitation);
      setShowModal(true);
      mediumImpact();
    } else {
      // Show toast for future games
      showNotification(
        t('game_invitation_body', {
          username: invitation.senderName,
          gameName: invitation.courseName || 'golf'
        }),
        t('game_invitation_title'),
        'golf-outline',
        () => window.location.href = '/notifications'
      );
    }
  };

  // Handle invitation acceptance
  const handleAcceptInvitation = async (invitationId: string) => {
    if (!userId) return;

    try {
      // Update the invitation status in the database
      const { error } = await supabase
        .from('gameInvitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId);
        
      if (error) throw error;
      
      // Redirect to the game page
      window.location.href = `/games/${invitationId}`;
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      showNotification(t('error_accepting_invitation'), t('error'), 'alert-circle-outline');
    }
  };

  // Handle invitation decline
  const handleDeclineInvitation = async (invitationId: string) => {
    if (!userId) return;

    try {
      // Update the invitation status in the database
      const { error } = await supabase
        .from('gameInvitations')
        .update({ status: 'declined' })
        .eq('id', invitationId);
        
      if (error) throw error;
      
      // Close the modal
      setShowModal(false);
      setCurrentInvitation(null);
      // Show success message
      showNotification(t('invitation_declined'), t('success'), 'checkmark-circle-outline');
    } catch (error: any) {
      console.error('Error declining invitation:', error);
      showNotification(t('error_declining_invitation'), t('error'), 'alert-circle-outline');
    }
  };

  // Listen for new notifications in Supabase when the app is active
  useEffect(() => {
    if (!userId) return;

    const lastNotificationTime = Date.now();
    let notificationsChannel: RealtimeChannel;

    // Set up Supabase realtime subscription
    const setupRealtimeSubscription = async () => {
      // Subscribe to notifications table changes for this user
      notificationsChannel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          async (payload) => {
            // New notification received
            const newNotification = payload.new;
            
            // Only show notification for new items that came in while the app was active
            if (newNotification && new Date(newNotification.created_at).getTime() > lastNotificationTime) {
              // Get sender info
              const senderId = newNotification.data?.senderId;
              if (!senderId) return;
              
              try {
                // Fetch sender profile from API
                const response = await api.get(`/profiles/${senderId}`);
                if (!response.data) return;
                
                const sender = response.data;
                const senderName = sender.firstName || sender.username;

                // Trigger haptic feedback
                mediumImpact();

                // Handle different notification types
                switch (newNotification.type) {
                  case 'friend_request':
                    showNotification(
                      t('friend_request_notification', { name: senderName }),
                      t('friend_request'),
                      'person-add-outline'
                    );
                    break;
                  case 'friend_request_accepted':
                    showNotification(
                      t('friend_request_accepted_notification', { name: senderName }),
                      t('friend_request_accepted'),
                      'checkmark-circle-outline'
                    );
                    break;
                  case 'post_like':
                    showNotification(
                      t('post_like_notification', { name: senderName }),
                      t('notification_like'),
                      'heart-outline'
                    );
                    break;
                  case 'post_comment':
                    showNotification(
                      t('post_comment_notification', { name: senderName }),
                      t('notification_comment'),
                      'chatbubble-outline'
                    );
                    break;
                  case 'new_post':
                    showNotification(
                      t('new_post_notification', { name: senderName }),
                      t('new_post'),
                      'image-outline'
                    );
                    break;
                  case 'game_invitation':
                    // Show game invitation modal
                    const gameData = newNotification.data;
                    if (gameData) {
                      const invitation: GameInvitation = {
                        id: newNotification.id.toString(),
                        senderId: senderId,
                        senderName: senderName,
                        senderHcp: sender.handicap || 0,
                        courseName: gameData.courseName || 'Golf Course',
                        courseLocation: gameData.courseLocation,
                        gameDate: gameData.gameDate || new Date().toISOString(),
                        gameTime: gameData.gameTime || '12:00',
                        gameType: gameData.gameType || 'Stroke Play',
                        players: gameData.players || 1,
                        message: gameData.message
                      };
                      showGameInvitation(invitation);
                    }
                    break;
                  default:
                    showNotification(
                      t('new_notification'),
                      t('notification'),
                      'notifications-outline'
                    );
                }
              } catch (error) {
                console.error('Error fetching sender profile:', error);
              }
            }
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    return () => {
      // Clean up Supabase subscription
      if (notificationsChannel) {
        supabase.removeChannel(notificationsChannel);
      }
    };
  }, [userId, t, showNotification, showGameInvitation]);

  return (
    <NotificationContext.Provider value={{ showNotification, showGameInvitation }}>
      {children}
      {/* Game invitation modal */}
      <GameInvitationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        invitation={currentInvitation}
        onAccept={handleAcceptInvitation}
        onDecline={handleDeclineInvitation}
      />
    </NotificationContext.Provider>
  );
};
